// Data layer for the v2 planner. Talks to the SAME backend as v1:
//   GET   /:user/plans/:slug/data  -> Plan#data (version-specific JSON)
//   PATCH /:user/plans/:slug       -> save (plan[content] = v4)
// Migrates older plan data to v4 in memory before it renders.

import { migrate } from './migrations';
import { parseSaveErrorBody } from '../lib/plan_save_errors';

export function csrfToken() {
  return document.querySelector('meta[name="csrf-token"]')?.content || '';
}

// A plan-data load that returned no body we could use. Carries the HTTP status
// (null when the fetch itself never resolved) so callers report the detail as an
// event property instead of baking it into the exception name. A body the
// browser could not finish reading — a dropped mobile connection words this as
// an engine-specific JSON parse error — is a transport failure, not a defect in
// our plan data contract, so it sets `isNetworkError` and callers skip filing it
// as a bug, exactly as they do for a fetch that never reached the server.
export class PlanLoadError extends Error {
  constructor(message, { status = null, isNetworkError = false } = {}) {
    super(message);
    this.name = 'PlanLoadError';
    this.status = status;
    this.isNetworkError = isNetworkError;
  }
}

export async function loadPlanData(dataUrl) {
  let response;
  try {
    response = await fetch(dataUrl, {
      headers: { Accept: 'application/json', 'X-CSRF-Token': csrfToken() },
    });
  } catch (_) {
    throw new PlanNetworkError();
  }
  if (!response.ok) {
    throw new PlanLoadError(`Failed to load plan (${response.status})`, { status: response.status });
  }
  let data;
  try {
    data = await response.json();
  } catch (_) {
    throw new PlanLoadError(`Could not read plan data (${response.status})`, {
      status: response.status,
      isNetworkError: true,
    });
  }
  return migrate(data);
}

// A fetch that never reached the server: the connection dropped, the user
// navigated away mid-request, or something blocked the call. This is not a
// defect in our save logic, so callers report it apart from server errors.
export class PlanNetworkError extends Error {
  constructor() {
    super('Check your connection and try again.');
    this.name = 'PlanNetworkError';
    this.isNetworkError = true;
  }
}

// A save the server rejected. Carries the HTTP status so callers can report it
// as an event property instead of baking it into the exception name.
export class PlanSaveError extends Error {
  constructor(message, status, sessionExpired = false) {
    super(message);
    this.name = 'PlanSaveError';
    this.status = status;
    this.sessionExpired = sessionExpired;
  }
}

// Send a plan via fetch (not p5.httpDo) so we can read validation errors —
// mirrors the pattern shipped for v1 in PR #88. Resolves to the parsed JSON on
// success. Throws PlanNetworkError on a network-level rejection, or
// PlanSaveError (with the server's reason and status) on a non-2xx response.
async function submitPlan(url, method, payload) {
  let response;
  try {
    response = await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-Token': csrfToken() },
      body: JSON.stringify(payload),
    });
  } catch (_) {
    throw new PlanNetworkError();
  }
  if (!response.ok) {
    let body = null;
    try { body = await response.json(); } catch (_) { /* non-JSON error body */ }
    const { message, sessionExpired } = parseSaveErrorBody(body, response.status);
    throw new PlanSaveError(message, response.status, sessionExpired);
  }
  return response.json();
}

// Save an existing plan (PATCH /:user/plans/:slug).
export function savePlan(saveUrl, payload) {
  return submitPlan(saveUrl, 'PATCH', payload);
}

// Create a new plan (POST /plans). Returns the parsed JSON ({ uri, ... }).
export function createPlan(createUrl, payload) {
  return submitPlan(createUrl, 'POST', payload);
}

// LocalStorage draft. Account and guest drafts use separate keys.
export function draftKey(username, slug) { return `plans.${username || ''}.${slug}`; }

export const DRAFT_HANDOFF_TTL_MS = 30 * 60 * 1000;
export const DRAFT_HANDOFF_MARKER_KEY = 'plans.handoff.marker';
const DRAFT_HANDOFF_KEY_PREFIX = 'plans.handoff.';

function draftHandoffKey(token) { return `${DRAFT_HANDOFF_KEY_PREFIX}${token}`; }

function newHandoffToken() {
  try {
    if (typeof globalThis.crypto?.randomUUID === 'function') return globalThis.crypto.randomUUID();
    if (typeof globalThis.crypto?.getRandomValues === 'function') {
      const bytes = new Uint8Array(16);
      globalThis.crypto.getRandomValues(bytes);
      return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
    }
  } catch (_) { /* use the browser-compatible fallback below */ }

  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
}

function validHandoffToken(token) {
  return typeof token === 'string' && /^[a-zA-Z0-9_-]{16,128}$/.test(token);
}

function removeHandoff(token) {
  try { sessionStorage.removeItem(DRAFT_HANDOFF_MARKER_KEY); } catch (_) { /* ignore */ }
  if (!validHandoffToken(token)) return;
  try { localStorage.removeItem(draftHandoffKey(token)); } catch (_) { /* ignore */ }
}

export function saveDraft(username, slug, content) {
  try {
    localStorage.setItem(draftKey(username, slug), JSON.stringify(content));
    return true;
  } catch (_) {
    return false;
  }
}

export function saveDraftHandoff(content, token = newHandoffToken(), now = Date.now()) {
  if (!validHandoffToken(token)) return false;

  try {
    const previous = JSON.parse(sessionStorage.getItem(DRAFT_HANDOFF_MARKER_KEY));
    if (previous?.token && previous.token !== token) removeHandoff(previous.token);
  } catch (_) {
    try { sessionStorage.removeItem(DRAFT_HANDOFF_MARKER_KEY); } catch (_) { /* ignore */ }
  }

  const key = draftHandoffKey(token);
  try {
    localStorage.setItem(key, JSON.stringify({ token, content }));
    sessionStorage.setItem(DRAFT_HANDOFF_MARKER_KEY, JSON.stringify({ token, createdAt: now }));
    return true;
  } catch (_) {
    removeHandoff(token);
    return false;
  }
}

// A handoff is valid only in the tab that started authentication. Keep its
// token-scoped copy until the user restores or discards it, so a reload while
// the restore banner is visible cannot lose the draft.
export function loadDraftHandoff(username, slug, now = Date.now()) {
  if (!username || slug !== 'new') return null;

  let marker;
  try {
    const rawMarker = sessionStorage.getItem(DRAFT_HANDOFF_MARKER_KEY);
    if (!rawMarker) return null;
    marker = JSON.parse(rawMarker);
  } catch (_) {
    try { sessionStorage.removeItem(DRAFT_HANDOFF_MARKER_KEY); } catch (_) { /* ignore */ }
    return null;
  }

  const token = marker?.token;
  const createdAt = marker?.createdAt;
  const fresh = validHandoffToken(token) && Number.isFinite(createdAt) &&
    now >= createdAt && now - createdAt <= DRAFT_HANDOFF_TTL_MS;
  if (!fresh) {
    removeHandoff(token);
    return null;
  }

  try {
    const rawDraft = localStorage.getItem(draftHandoffKey(token));
    if (!rawDraft) {
      removeHandoff(token);
      return null;
    }
    const envelope = JSON.parse(rawDraft);
    if (envelope?.token !== token || !Object.prototype.hasOwnProperty.call(envelope, 'content')) {
      removeHandoff(token);
      return null;
    }
    return { draft: envelope.content, token };
  } catch (_) {
    removeHandoff(token);
    return null;
  }
}

export function clearDraftHandoff(token) {
  removeHandoff(token);
}

export function loadDraft(username, slug) {
  try {
    const value = localStorage.getItem(draftKey(username, slug));
    return value ? JSON.parse(value) : null;
  } catch (_) {
    return null;
  }
}

export function loadDraftCandidate(username, slug, now = Date.now()) {
  const handoff = loadDraftHandoff(username, slug, now);
  if (handoff) return { draft: handoff.draft, handoff: true, handoffToken: handoff.token };

  const draft = loadDraft(username, slug);
  return draft ? { draft, handoff: false, handoffToken: null } : null;
}

export function clearDraft(username, slug) {
  try { localStorage.removeItem(draftKey(username, slug)); } catch (_) { /* ignore */ }
}
